import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SearchOutlined } from '@ant-design/icons';
import { axiosInstance } from '../api';
import { IMPORT_COUNTRIES } from '../AddUserListing/constants/listingOptions';

const SearchModule: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [keyword, setKeyword] = useState('');
  const [isImported, setIsImported] = useState(false);
  const [importCountry, setImportCountry] = useState('');

  const [makeOptions, setMakeOptions] = useState<string[]>([]);
  const [modelOptions, setModelOptions] = useState<string[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);

  useEffect(() => {
    axiosInstance.get('/api/vehicles/makes')
      .then(res => setMakeOptions(Array.isArray(res.data) ? res.data : []))
      .catch(() => setMakeOptions([]));
  }, []);

  useEffect(() => {
    if (!make) { setModelOptions([]); setModel(''); return; }
    setLoadingModels(true);
    axiosInstance.get('/api/vehicles/models', { params: { make } })
      .then(res => setModelOptions(Array.isArray(res.data) ? res.data : []))
      .catch(() => setModelOptions([]))
      .finally(() => setLoadingModels(false));
  }, [make]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (make) params.set('make', make);
    if (model) params.set('model', model);
    if (keyword.trim()) params.set('searchQuery', keyword.trim());
    if (isImported) {
      params.set('isImported', 'true');
      if (importCountry) params.set('importCountry', importCountry);
    }
    navigate(`/mysearch?${params.toString()}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <div className="container" style={{ marginTop: '-250px' }}>
      <div className="hp-search-card" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
        <div
          role="tablist"
          aria-label="Vehicle source"
          style={{ display: 'flex', gap: 0, borderBottom: '1px solid rgba(0,0,0,0.1)', marginBottom: 12 }}
        >
          {[
            { value: false, label: t('quick_search.all_vehicles') },
            { value: true, label: t('quick_search.imported_vehicles') },
          ].map((opt) => {
            const active = isImported === opt.value;
            return (
              <button
                key={String(opt.value)}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setIsImported(opt.value)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: active ? '#fe5a1d' : '#6b7280',
                  fontWeight: active ? 700 : 500,
                  fontSize: 14,
                  padding: '10px 18px',
                  cursor: 'pointer',
                  borderBottom: active ? '2px solid #fe5a1d' : '2px solid transparent',
                  marginBottom: -1,
                  transition: 'color 0.15s ease',
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="hp-search-group">
          <label>{t('search.label_make')}</label>
          <select
            className="hp-search-control"
            value={make}
            onChange={e => { setMake(e.target.value); setModel(''); }}
          >
            <option value="">{t('search.any_make')}</option>
            {makeOptions.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>

        <div className="hp-search-group">
          <label>{t('search.label_model')}</label>
          <select
            className="hp-search-control"
            value={model}
            onChange={e => setModel(e.target.value)}
            disabled={!make || loadingModels}
          >
            <option value="">{make ? t('search.any_model') : t('search.select_make_first')}</option>
            {modelOptions.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>

        <div className="hp-search-group">
          <label>{t('search.label_keyword')}</label>
          <input
            type="text"
            className="hp-search-control"
            placeholder={t('search.keyword_ph')}
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>

        {isImported && (
          <div className="hp-search-group">
            <label>{t('search.label_import_country')}</label>
            <select
              className="hp-search-control"
              value={importCountry}
              onChange={e => setImportCountry(e.target.value)}
            >
              <option value="">{t('search.any_country')}</option>
              {IMPORT_COUNTRIES.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        )}

        <button className="hp-search-btn" onClick={handleSearch}>
          <SearchOutlined />
        </button>
        </div>
      </div>
    </div>
  );
};

export default SearchModule;
